import { apiClient } from './client';

// ── Tipos ─────────────────────────────────────────────────────────────────────

/** Protocolos de insumos con costo (se pueden aplicar como presupuesto) */
export type LegacyProtocolType = 'QUIMICO' | 'FERTILIZANTE' | 'AMBOS';
/** Marcos: calendario editable por día de siembra, sin costos */
export type MarcoType = 'MARCO_FERTILIZACION' | 'MARCO_PULVERIZACION';
export type ProtocolType = LegacyProtocolType | MarcoType;

export const isMarcoType = (t: ProtocolType | undefined): t is MarcoType =>
  t === 'MARCO_FERTILIZACION' || t === 'MARCO_PULVERIZACION';

export interface ProtocolProductDose {
  name:      string;
  dose:      string;
  doseQty?:  number;
  doseUnit?: string;
}

export interface ProtocolApplication {
  dias:         string;
  productos:    ProtocolProductDose[];
  costoHa:      number;
  etapa?:       string;
  observacion?: string;
}

export interface ProtocolCatalogItem {
  name:           string;
  presentacion:   string;
  precioUnitario: number;
  category:       'AGROQUIMICO' | 'FERTILIZANTE';
}

export interface MarcoFertilizacionColumna {
  key:    string;
  nombre: string;
  unidad: string;
}

export interface MarcoFertilizacionFila {
  /** Días de siembra (-1 = fertilizante base) */
  dias:         number;
  tipo:         string;
  cantidades:   Record<string, number>;
  observacion?: string;
}

export interface MarcoFertilizacion {
  columnas: MarcoFertilizacionColumna[];
  filas:    MarcoFertilizacionFila[];
}

export interface ProtocolRecord {
  protocolId:          string;
  tenantId:            string;
  ranchoId?:           string;
  crop?:               string;
  ciclo?:              string;
  nombre?:             string;
  type:                ProtocolType;
  aplicaciones:        ProtocolApplication[];
  catalogo:            ProtocolCatalogItem[];
  costoQuimicoHa:      number;
  costoFertilizanteHa: number;
  costoTotalHa:        number;
  createdAt:           string;
  createdBy:           string;
  source:              'TELEGRAM_DOC' | 'TELEGRAM_PHOTO' | 'TELEGRAM_TEXT' | 'WEB' | 'IMPORT_XLSX';
  productIdsUpserted?: string[];
  marcoFertilizacion?: MarcoFertilizacion;
}

// ── Queries ───────────────────────────────────────────────────────────────────

/** Lista protocolos del tenant (mismo endpoint que FinancePage usa) */
export const fetchProtocols = async (
  opts?: { crop?: string; ranchoId?: string },
): Promise<ProtocolRecord[]> =>
  (await apiClient.get('/api/protocols', { params: opts })).data;

// ── Mutaciones ────────────────────────────────────────────────────────────────

export interface CreateProtocolInput {
  ranchoId?:           string;
  crop?:               string;
  ciclo?:              string;
  nombre?:             string;
  type:                ProtocolType;
  aplicaciones:        ProtocolApplication[];
  catalogo?:           ProtocolCatalogItem[];
  costoQuimicoHa?:     number;
  costoFertilizanteHa?: number;
  costoTotalHa?:       number;
  marcoFertilizacion?: MarcoFertilizacion;
}

export const createProtocol = async (input: CreateProtocolInput): Promise<ProtocolRecord> =>
  (await apiClient.post('/api/protocols', input)).data;

export const updateProtocol = async (protocolId: string, input: Partial<CreateProtocolInput>): Promise<ProtocolRecord> =>
  (await apiClient.put(`/api/protocols/${protocolId}`, input)).data;

export const deleteProtocol = async (protocolId: string): Promise<void> =>
  (await apiClient.delete(`/api/protocols/${protocolId}`)).data;

export interface DuplicateProtocolInput {
  nombre?: string;
  crop?:   string;
  ciclo?:  string;
}

export const duplicateProtocol = async (protocolId: string, input: DuplicateProtocolInput): Promise<ProtocolRecord> =>
  (await apiClient.post(`/api/protocols/${protocolId}/duplicate`, input)).data;

// ── Importación desde Excel ───────────────────────────────────────────────────

export interface ProtocolImportPreviewItem {
  protocolId:    string;
  sheet:         string;
  type:          MarcoType;
  nombre:        string;
  productos?:    number;
  tiempos?:      number;
  aplicaciones?: number;
  /** Ya hay un marco con ese nombre en la empresa */
  exists:        boolean;
}

export interface ProtocolImportError { sheet: string; message: string }

export interface ProtocolImportResponse {
  dryRun?: boolean;
  errors:  ProtocolImportError[];
  summary: { total: number; toCreate: number; existing: number; created: number; replaced: number; skipped: number };
  preview?: ProtocolImportPreviewItem[];
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function runProtocolImport(file: File, dryRun: boolean, overwrite: string[] = []): Promise<ProtocolImportResponse> {
  const fileBase64 = await fileToBase64(file);
  return (await apiClient.post<ProtocolImportResponse>('/api/protocols/import', { fileBase64, dryRun, overwrite })).data;
}

/** Valida el Excel sin escribir: devuelve los marcos detectados y cuáles ya existen. */
export const previewProtocolImport = (file: File) => runProtocolImport(file, true);
/** Crea los marcos nuevos; los ya existentes solo se reemplazan si su id viene en `overwrite`. */
export const commitProtocolImport = (file: File, overwrite: string[]) => runProtocolImport(file, false, overwrite);

export interface ApplyProtocolInput {
  loteId:     string;
  ranchoId?:  string;
  hectareas:  number;
  ciclo:      string;
  /** Zona (módulo) del lote: con ella y el proyecto y la fecha, se generan plan y tareas. */
  moduleId?:     string;
  projectId?:    string;
  /** Fecha de siembra, `YYYY-MM-DD`. */
  plantingDate?: string;
}

export const applyProtocol = async (protocolId: string, input: ApplyProtocolInput): Promise<unknown> =>
  (await apiClient.post(`/api/protocols/${protocolId}/apply`, input)).data;
